import { useState, useCallback, useEffect, useRef } from "react";
import { apiClient, ApiError } from "../services/api";
import type {
  User,
  VideoListResponse,
  VideoGenerationRequest,
  ImageGenerationRequest,
  VideoStatusResponse,
} from "../types/api";

// Generic hook for API calls with loading and error states
export function useApiCall<T>(
  apiCall: () => Promise<T>,
  dependencies: any[] = []
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall();
      setData(result);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "An unexpected error occurred"
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, ...dependencies]);

  useEffect(() => {
    execute();
  }, [execute]);

  return { data, loading, error, refetch: execute };
}

// Hook for managing user data (disabled for showcase)
export function useUser() {
  const [userData] = useState<User | null>(null);
  const [loading] = useState(false);
  const [error] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    // Disabled for showcase site
  }, []);

  return {
    user: userData,
    loading,
    error,
    refetch: fetchUser,
  };
}

// Hook for managing videos (disabled for showcase)
export function useVideos(page: number = 1, perPage: number = 20) {
  const [videos] = useState<VideoListResponse | null>(null);
  const [loading] = useState(false);
  const [error] = useState<string | null>(null);
  const [showDelayedMessage] = useState(false);

  const fetchVideos = useCallback(async () => {
    // Disabled for showcase site
  }, []);

  const deleteVideo = useCallback(
    async (videoId: string) => {
      // Disabled for showcase site
      throw new Error("Not available in showcase mode");
    },
    []
  );

  return {
    videos,
    loading,
    error,
    showDelayedMessage,
    refetch: fetchVideos,
    deleteVideo,
  };
}

// Hook for video generation with polling (disabled for showcase)
export function useVideoGeneration(onGenerationComplete?: () => void) {
  const [generationState, setGenerationState] = useState({
    loading: false,
    error: null as string | null,
    progress: null as VideoStatusResponse | null,
    completed: null as VideoStatusResponse | null,
  });

  const pollControllerRef = useRef<AbortController | null>(null);

  const generateVideo = useCallback(
    async (_request: Omit<VideoGenerationRequest, "clerk_id">) => {
      // Disabled for showcase site
      throw new Error("Video generation not available in showcase mode");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const cancelGeneration = useCallback(() => {
    if (pollControllerRef.current) {
      pollControllerRef.current.abort();
    }
    setGenerationState((prev) => ({
      ...prev,
      loading: false,
      error: null,
    }));
  }, []);

  const reset = useCallback(() => {
    if (pollControllerRef.current) {
      pollControllerRef.current.abort();
    }
    setGenerationState({
      loading: false,
      error: null,
      progress: null,
      completed: null,
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    const controller = pollControllerRef.current;
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, []);

  return {
    ...generationState,
    generateVideo,
    cancelGeneration,
    reset,
  };
}

// Hook for image generation (disabled for showcase)
export function useImageGeneration(_onGenerationComplete?: () => void) {
  const [generationState, setGenerationState] = useState({
    loading: false,
    error: null as string | null,
    completed: null as any,
  });

  const generateImage = useCallback(
    async (_request: Omit<ImageGenerationRequest, "clerk_id">) => {
      // Disabled for showcase site
      throw new Error("Image generation not available in showcase mode");
    },
    []
  );

  const reset = useCallback(() => {
    setGenerationState({
      loading: false,
      error: null,
      completed: null,
    });
  }, []);

  return {
    ...generationState,
    generateImage,
    reset,
  };
}

// Hook for exporting/downloading videos
export function useVideoExport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportVideo = useCallback(
    async (videoId?: string, operationId?: string) => {
      if (!videoId && !operationId) {
        throw new Error("Either videoId or operationId must be provided");
      }

      try {
        setLoading(true);
        setError(null);

        const result = await apiClient.exportVideo({
          ...(videoId && { video_id: videoId }),
          ...(operationId && { operation_id: operationId }),
        });

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof ApiError ? err.message : "Failed to export video";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    loading,
    error,
    exportVideo,
  };
}

// Hook for checking if user can generate videos (has credits)
export function useCanGenerate() {
  const { user } = useUser();

  const canGenerate = user
    ? user.limits.credits < user.limits.max_credits
    : false;

  const remainingCredits = user
    ? user.limits.max_credits - user.limits.credits
    : 0;

  const usedCredits = user ? user.limits.credits : 0;

  const maxCredits = user ? user.limits.max_credits : 0;

  return {
    canGenerate,
    remainingCredits,
    usedCredits,
    maxCredits,
  };
}
